'use client';

import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { authService } from '../services/auth.service';
import type { ErrorCollection, Profile } from '../types/auth.types';
import { createClient } from '../supabase/client';
import { get } from '../api/rest-client';
interface LoginError {
  error: ErrorCollection;
}

interface LoginSuccess {
  data: Profile;
}

type LoginResult = LoginSuccess | LoginError;

export function useAuthMutations() {
  const router = useRouter();
  const supabase = createClient();

  const { mutateAsync: login, isPending: isLoginPending } = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }): Promise<LoginResult> => {
      try {
        const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (signInError) {
          throw signInError;
        }

        if (!authData.user || !authData.session) {
          throw new Error('Login failed: No user or session data returned.');
        }

        const profileData = await get<Profile>(`/profiles/${authData.user.id}`);

        if (!profileData) {
          throw new Error('Profile not found');
        }

        return { data: profileData };
      } catch (error: any) {
        if (error.message === 'User is not authorized') {
          return {
            error: {
              general: ['User is not authorized'],
            },
          };
        }

        if (error.response?.data) {
          return { error: error.response.data };
        }

        return {
          error: {
            general: ['An unexpected error occurred'],
          },
        };
      }
    },
  });

  const { mutateAsync: forgotPassword, isPending: isForgotPasswordPending } = useMutation({
    mutationFn: async (email: string) => {
      try {
        console.log('forgotPassword email', email);
        const result = await authService.forgotPassword(email);
        console.log('forgotPassword result', result);
        return { data: result };
      } catch (error: any) {
        console.log('forgotPassword error', error);
        if (error.response?.data) {
          return { error: error.response.data };
        }
        return {
          error: {
            general: ['An unexpected error occurred'],
          },
        };
      }
    },
  });

  const { mutateAsync: resetPassword, isPending: isResetPasswordPending } = useMutation({
    mutationFn: async ({ oldPassword, newPassword }: { oldPassword: string; newPassword: string }) => {
      try {
        const result = await authService.resetPassword(oldPassword, newPassword);
        console.log('resetPassword result', result);
        return { data: result };
      } catch (error: any) {
        console.log('resetPassword error', error);
        if (error.response?.data) {
          return { error: error.response.data };
        }
        return {
          error: {
            general: ['An unexpected error occurred'],
          },
        };
      }
    },
  });

  const { mutateAsync: resetPasswordFromEmail, isPending: isResetPasswordFromEmailPending } = useMutation({
    mutationFn: async ({ email, newPassword }: { email: string; newPassword: string }) => {
      try {
        console.log('resetPasswordFromEmail email', email);
        const result = await authService.resetPasswordFromEmail(email, newPassword);
        console.log('resetPasswordFromEmail result', result);
        return { data: result };
      } catch (error: any) {
        console.log('resetPasswordFromEmail error', error);
        if (error.response?.data) {
          return { error: error.response.data };
        }
        return {
          error: {
            general: ['An unexpected error occurred'],
          },
        };
      }
    },
  });

  const { mutateAsync: signup, isPending: isSignupPending } = useMutation({
    mutationFn: async (payload: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      // Add other necessary fields
    }): Promise<{ data?: Profile; error?: ErrorCollection }> => {
      try {
        const profile = await authService.signup(payload);
        // Optionally, perform auto-login here if needed by calling authService.login
        return { data: profile };
      } catch (error: any) {
        if (error.response?.data) {
          return { error: error.response.data };
        }
        return {
          error: {
            general: ['An unexpected error occurred during sign up'],
          },
        };
      }
    },
    onSuccess: () => {
      // Maybe redirect to login or show a success message
      router.push('/login?signup=success'); // Example redirection
    },
  });

  const { mutate: logout } = useMutation({
    mutationFn: () => {
      authService.logout();
      return Promise.resolve();
    },
  });

  return {
    login,
    isLoginPending,
    forgotPassword,
    isForgotPasswordPending,
    resetPassword,
    isResetPasswordPending,
    resetPasswordFromEmail,
    isResetPasswordFromEmailPending,
    signup,
    isSignupPending,
    logout,
  };
}
